import React, { useState, useEffect } from 'react';
import ModernChatInterface from './ModernChatInterface';
import { generateConversationId } from '../utils/helpers';
import './ModernLanding.css';

function ModernLanding({ community }) {
  const [conversationId] = useState(generateConversationId());
  const [inputFocused, setInputFocused] = useState(false);
  const [showExamples, setShowExamples] = useState(true);
  
  // Friendly example prompts for non-tech users
  const examplePrompts = [
    {
      icon: "🍕",
      text: "Where can I get the best pizza?",
      category: "food"
    },
    {
      icon: "🏥",
      text: "Find a family doctor near me",
      category: "health"
    },
    {
      icon: "🛍️",
      text: "What stores are open today?",
      category: "shopping"
    },
    {
      icon: "🎉",
      text: "What events are happening this weekend?",
      category: "events"
    },
    {
      icon: "🏠",
      text: "Tell me about moving to " + community.name,
      category: "living"
    },
    {
      icon: "💼",
      text: "Show me local job opportunities",
      category: "work"
    }
  ];

  const handleExampleClick = (prompt) => {
    // This will be handled by the ChatInterface
    const input = document.querySelector('.chat-input textarea');
    if (input) {
      input.value = prompt.text;
      input.focus();
      setShowExamples(false);
    }
  };

  return (
    <div className="modern-landing">
      {/* Simplified Header */}
      <header className="modern-header">
        <div className="header-container">
          <div className="logo-section">
            <div className="ai-badge">AI</div>
            <div>
              <h1 className="site-name">{community.name}</h1>
              <p className="site-tagline">Your friendly local assistant</p>
            </div>
          </div>
          
          <div className="community-stats">
            {community.population && (
              <div className="stat-pill">
                <span className="stat-emoji">👥</span>
                <span className="stat-text">{community.population.toLocaleString()} residents</span>
              </div>
            )}
            {community.businessCount && (
              <div className="stat-pill">
                <span className="stat-emoji">🏪</span>
                <span className="stat-text">{community.businessCount} businesses</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="modern-main">
        {/* Welcome Section */}
        <div className={`welcome-section ${!showExamples ? 'collapsed' : ''}`}>
          <div className="welcome-content">
            <h2 className="welcome-title">
              Hello! I'm here to help you explore {community.name} 👋
            </h2>
            <p className="welcome-subtitle">
              Ask me anything about local businesses, services, events, or life in our community.
              I speak plain English - no tech knowledge needed!
            </p>
          </div>

          {/* Example Cards */}
          {showExamples && (
            <div className="example-cards">
              <p className="examples-label">Try asking:</p>
              <div className="examples-grid">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    className={`example-card ${prompt.category}`}
                    onClick={() => handleExampleClick(prompt)}
                  >
                    <span className="example-icon">{prompt.icon}</span>
                    <span className="example-text">{prompt.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chat Section */}
        <div className="chat-section">
          <div className={`chat-container ${inputFocused ? 'focused' : ''}`}>
            <ModernChatInterface
              conversationId={conversationId}
              community={community}
              placeholder={`Ask me anything about ${community.name}...`}
              onInputFocus={() => setInputFocused(true)}
              onInputBlur={() => setInputFocused(false)}
              showWelcomeMessage={!showExamples}
            />
          </div>
        </div>

        {/* Helper Tips */}
        <div className="helper-tips">
          <div className="tip">
            <span className="tip-icon">💡</span>
            <span className="tip-text">
              Tip: I can help with directions, hours, reviews, and local recommendations!
            </span>
          </div>
        </div>
      </main>

      {/* Simplified Footer */}
      <footer className="modern-footer">
        <div className="footer-content">
          <p className="footer-text">
            Made with ❤️ for the {community.name} community
          </p>
          <div className="footer-links">
            <button className="footer-link">Help</button>
            <span className="footer-divider">•</span>
            <button className="footer-link">Privacy</button>
            <span className="footer-divider">•</span>
            <button className="footer-link">Feedback</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ModernLanding;